import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useLanguage } from "@/clientWeb/i18n/LanguageContext"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { FullPageDotsLoader } from "@/clientWeb/components/ui/page-loader"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { useMeetingsStore } from "../../store/meetings.store"
import { meetingsApi } from "../../services/meetings.api"
import { meetingsRepo } from "../../services/meetings.repo"
import { joinByIdOutcome } from "../../services/join-failure"
import { MEETING_ID_LENGTH } from "../../constants/meetings.constants"

interface JoinMeetingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Asks the server whether the entered meeting ID can be joined before sending
 * the user to the VC client, and says why not when it cannot: full, cancelled,
 * ended, not today's meeting, or not found.
 *
 * A password-protected meeting goes straight on: the meeting page asks for the
 * password, as it does for anyone opening the meeting link.
 */
export default function JoinMeetingDialog({ open, onOpenChange }: JoinMeetingDialogProps) {
  const { t, language } = useLanguage()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)

  // Only signed-in users reach this dialog, so we already know who they are.
  // Asking them to retype it added a field that could disagree with their
  // account and told the meeting nothing new.
  const name = user?.name ?? ''
  const [meetingId, setMeetingId] = useState('')
  const [errors, setErrors] = useState<{ name?: string; meetingId?: string; form?: string }>({})
  const [submitting, setSubmitting] = useState(false)

  /**
   * Empty the form once the dialog is closed.
   *
   * The parent owns `open` and this component is never unmounted, so its state
   * outlived every close: reopening Join Meeting showed the id from last time,
   * still filled in, along with whatever error it had failed with. Someone
   * joining a second meeting had to clear the field themselves, and an id left
   * over from a mistyped attempt looked like a suggestion.
   *
   * Keyed on `open` going false rather than on the close handler, because
   * closing happens four ways — Cancel, the X, Escape, and a successful join —
   * and only two of them pass through anything this component owns.
   */
  useEffect(() => {
    if (open) return
    setMeetingId('')
    setErrors({})
    setSubmitting(false)
  }, [open])

  /**
   * The field is numbers-only and enforces it by stripping, which means a typed
   * letter never reaches the input and nothing on screen changes. That silence
   * read as a dead keyboard rather than as a rule, so the rejection is now said
   * out loud: anything non-digit in what arrived sets the field's own error,
   * which the next acceptable keystroke clears again.
   */
  const handleMeetingIdChange = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, MEETING_ID_LENGTH)
    setMeetingId(digits)
    setErrors((p) => ({
      ...p,
      meetingId: /\D/.test(val) ? t('meetings.errors.ID_NUMBERS_ONLY') : undefined,
      form: undefined,
    }))
  }

  // No reload of the meeting list on the way out. This used to call the store's
  // `refresh`, which REPLACED the dashboard's server-loaded list with the
  // browser's stored copy — so coming back from the meeting showed whatever
  // localStorage held instead, a stored guest preview included, and that crashed
  // the meeting list (BUG-000533). The dashboard fetches from the server itself
  // when it opens again.
  const proceedToMeeting = (id: string, hostName: string) => {
    toast.success({
      title: t('meetings.joiningMeeting'),
      // No id here: `id` is the internal record id, which means nothing to the
      // person joining.
      description: `${t('meetings.connectingTo')}…`,
    })
    onOpenChange(false)
    navigate(`/meeting/${id}?name=${encodeURIComponent(hostName)}`)
  }

  /**
   * The server decides, not the browser's copy of the meeting.
   *
   * This used to fetch the meeting only when the browser had no copy of it and
   * then run the local mock `validateJoin` over that copy. The copy knows
   * nothing about seats and can be out of date, so a meeting that was simply
   * full was reported as "already been completed". `check: true` asks the same
   * question the meeting page asks, without joining or filing a lobby request.
   */
  const handleJoinMeeting = async () => {
    setErrors({})
    setSubmitting(true)
    try {
      const { meeting } = await meetingsApi.join(meetingId, { check: true })
      // The server answers an id it has no meeting for with `ok` and no
      // meeting — it still issues an ad-hoc room for it, for the old local-only
      // meetings. Nobody typing an id in here wants that room: the id is wrong.
      if (!meeting) {
        setErrors({ form: t('meetings.errors.MEETING_NOT_FOUND') })
        return
      }
      // Keep the dashboard's copy in step with what the server just said.
      // `addMeeting` leaves an existing local copy as it was, so a copy already
      // in the browser is overwritten instead.
      const store = useMeetingsStore.getState()
      if (meetingsRepo.findById(meeting.id)) store.updateMeeting(meeting.id, meeting)
      else store.addMeeting(meeting)
      proceedToMeeting(meeting.id, name.trim())
    } catch (err) {
      const outcome = joinByIdOutcome(err, t, language)
      if (outcome.kind === 'proceed') proceedToMeeting(meetingId, name.trim())
      else setErrors({ form: outcome.message })
    } finally {
      setSubmitting(false)
    }
  }

  const handleMeetingIdBlur = () => {
    // No-op; full validation runs on submit via the service. We keep this
    // hook to clear any stale error state on blur.
    if (!meetingId) setErrors((p) => ({ ...p, meetingId: undefined }))
  }

  return (
    <>
    {submitting && <FullPageDotsLoader message={t("meetings.joiningMeeting")} />}
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] sm:max-w-[480px] p-0 overflow-hidden bg-card dark:bg-card border-0 shadow-2xl"
        style={{ borderRadius: "16px" }}
      >
        {/* Header */}
        <DialogHeader className="p-3 sm:p-4 border-b border-stroke-100 dark:border-border dark:border-slate-800">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg sm:text-xl font-semibold text-slate-800 dark:text-white">
              {t('meetings.joinMeeting')}
            </DialogTitle>
          </div>
        </DialogHeader>

        {/* Form Content */}
        <div className="px-4 sm:px-6 space-y-4 sm:space-y-5">
          {/* Meeting ID */}
          <div>
            <label className="text-xs font-medium text-slate-400 dark:text-foreground tracking-wide mb-2 block">
              {t('meetings.meetingId')}
            </label>
            <Input
              value={meetingId}
              onChange={(e) => handleMeetingIdChange(e.target.value)}
              onBlur={handleMeetingIdBlur}
              variant="outlined"
              className="text-base sm:text-lg font-medium"
              placeholder={t('meetings.enterMeetingId')}
              inputMode="numeric"
              type="tel"
              maxLength={MEETING_ID_LENGTH}
              error={!!errors.meetingId}
              errorText={errors.meetingId}
            />
          </div>

          {errors.form && (
            <div className="rounded-lg bg-destructive/10 px-3 py-2.5">
              <p className="text-xs text-destructive">{errors.form}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-t border-stroke-100 dark:border-border dark:border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="px-4 sm:px-6 py-2.5 h-auto rounded-xl border-stroke-100 dark:border-border dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium transition-all duration-200 order-2 sm:order-1"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleJoinMeeting}
            // Codes are 9 digits (meeting-pin.ts), so `< 10` disabled the
            // button for every real one. Length is only a sanity check here —
            // the server decides whether the meeting exists.
            disabled={meetingId.trim().length < MEETING_ID_LENGTH || submitting}
            className="px-4 sm:px-6 py-2.5 h-auto rounded-xl bg-primary hover:bg-primary/90 text-white font-medium shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed order-1 sm:order-2"
          >
            {submitting ? t('common.joining') : t('meetings.joinMeeting')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    </>
  )
}
